/**
 * 商务风尚模版 - 注册页
 *
 * 设计理念：深墨绿品牌区 + 白色商务卡片（动态字段）
 * 与 sunset（写死字段 + 暖色左右分栏）完全不同：
 * - 字段完全由 GET /register/config 动态渲染（禁写死）
 * - 左侧：深墨绿品牌区（金色装饰 + 特性展示）
 * - 右侧：方角商务表单卡片（金色 focus 描边）
 * - 支持：动态字段 / 短信验证码 / 文件上传 / 协议弹窗
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";
import request, { getSinglePage } from "@/utils/api";

const getSiteId = () => localStorage.getItem('current_site_id') || '';

type FieldType = "text" | "textarea" | "select" | "radio" | "checkbox" | "password" | "number" | "date" | "file" | "image";

interface RegisterField {
  field_name: string;
  field_label: string;
  field_type: FieldType;
  placeholder: string;
  hint?: string;
  options?: string[];
  is_required: boolean;
  is_enabled: boolean;
  sort_order: number;
}

async function getRegisterConfig(siteId: string): Promise<{ fields: RegisterField[]; sms_enabled?: boolean }> {
  return request<{ fields: RegisterField[]; sms_enabled?: boolean }>(`/register/config`, {
    params: { site_id: siteId },
  });
}

async function registerApi(params: Record<string, string | undefined>) {
  return request("/auth/register", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

async function sendSmsCode(siteId: string, phone: string): Promise<void> {
  return request("/register/send-code", {
    method: "POST",
    params: { site_id: siteId },
    body: { phone },
  });
}

async function uploadFile(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  return request("/upload", {
    method: "POST",
    body: formData,
  });
}

export default function ExecutiveRegisterPage() {
  const { siteConfig } = useSiteConfig();
  const navigate = useNavigate();

  const [fields, setFields] = useState<RegisterField[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [uploadingFields, setUploadingFields] = useState<Record<string, boolean>>({});
  const formDataRef = useRef<Record<string, string>>({});

  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsLoading, setSmsLoading] = useState(false);

  const [agreementModal, setAgreementModal] = useState<{ show: boolean; title: string; content: string; loading: boolean }>({
    show: false, title: "", content: "", loading: false,
  });

  const siteLogo = siteConfig?.logo || "";
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "";

  const openAgreement = useCallback(async (slug: string, title: string) => {
    setAgreementModal({ show: true, title, content: "", loading: true });
    try {
      const page = await getSinglePage(slug);
      setAgreementModal({ show: true, title: page?.title || title, content: page?.content || "暂无内容", loading: false });
    } catch {
      setAgreementModal({ show: true, title, content: "加载失败，请稍后重试", loading: false });
    }
  }, []);

  const closeAgreement = useCallback(() => {
    setAgreementModal({ show: false, title: "", content: "", loading: false });
  }, []);

  // 加载注册配置（动态字段）
  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await getRegisterConfig(getSiteId());
        const enabledFields = config.fields.filter((f) => f.is_enabled);
        const initialData: Record<string, string> = {};
        enabledFields.forEach((field) => {
          initialData[field.field_name] = "";
        });
        if (config.sms_enabled) {
          initialData['phone'] = '';
          initialData['sms_code'] = '';
          setSmsEnabled(true);
        }
        setFields(enabledFields);
        setFormData(initialData);
        formDataRef.current = initialData;
      } catch (err) {
        console.error("加载注册配置失败:", err);
        setError("加载表单配置失败，请刷新重试");
      } finally {
        setIsLoadingConfig(false);
      }
    }
    loadConfig();
  }, []);

  // 短信倒计时
  useEffect(() => {
    if (smsCountdown > 0) {
      const timer = setTimeout(() => setSmsCountdown(smsCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [smsCountdown]);

  const handleSendSmsCode = useCallback(async () => {
    const phone = formDataRef.current['phone'] || '';
    if (!phone) { setError("请先输入手机号"); return; }
    if (!/^1[3-9]\d{9}$/.test(phone)) { setError("手机号格式不正确"); return; }
    try {
      setSmsLoading(true);
      await sendSmsCode(getSiteId(), phone);
      setSmsCountdown(60);
      setError("");
    } catch (err: any) {
      setError(err.message || "短信发送失败，请重试");
    } finally {
      setSmsLoading(false);
    }
  }, []);

  const handleChange = useCallback((fieldName: string, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [fieldName]: value };
      formDataRef.current = newData;
      return newData;
    });
  }, []);

  const togglePasswordVisibility = useCallback((fieldName: string) => {
    setShowPasswords((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  }, []);

  const handleFileUpload = useCallback(async (fieldName: string, file: File) => {
    try {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: true }));
      const result = await uploadFile(file);
      handleChange(fieldName, result.url);
    } catch (err) {
      console.error("文件上传失败:", err);
      alert("文件上传失败，请重试");
    } finally {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: false }));
    }
  }, [handleChange]);

  const getFieldIcon = (fieldType: FieldType) => {
    switch (fieldType) {
      case "text": case "number": return "ri-edit-line";
      case "textarea": return "ri-file-text-line";
      case "select": case "radio": case "checkbox": return "ri-list-check";
      case "password": return "ri-lock-line";
      case "date": return "ri-calendar-line";
      case "file": case "image": return "ri-image-add-line";
      default: return "ri-edit-line";
    }
  };

  const inputClass = "w-full pl-11 pr-4 py-3.5 bg-[#F7F8F6] border border-[#E2E8E0] rounded-sm text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#C2A04B] focus:ring-4 focus:ring-[#C2A04B]/10 transition-all";

  const renderField = (field: RegisterField) => {
    const value = formDataRef.current[field.field_name] || "";
    const isPassword = field.field_type === "password";
    const showPassword = showPasswords[field.field_name] || false;

    switch (field.field_type) {
      case "textarea":
        return (
          <textarea
            key={field.field_name}
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
            rows={3}
            className="w-full px-4 py-3 bg-[#F7F8F6] border border-[#E2E8E0] rounded-sm text-sm focus:outline-none focus:border-[#C2A04B] focus:ring-4 focus:ring-[#C2A04B]/10 transition-all resize-none"
          />
        );

      case "select":
        return (
          <select
            key={field.field_name}
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            className={`${inputClass} appearance-none cursor-pointer`}
          >
            <option value="">{field.placeholder || `请选择${field.field_label}`}</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case "radio":
        return (
          <div key={field.field_name} className="space-y-2">
            {field.options?.map((option) => (
              <label
                key={option}
                className={`flex items-center gap-2 p-3 rounded-sm border cursor-pointer transition-colors ${
                  value === option ? "border-[#C2A04B] bg-[#C2A04B]/5" : "border-[#E2E8E0] bg-[#F7F8F6]"
                }`}
              >
                <input
                  type="radio"
                  name={field.field_name}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleChange(field.field_name, e.target.value)}
                  className="w-4 h-4 accent-[#1F4E3D]"
                />
                <span className="text-sm text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case "checkbox":
        return (
          <div key={field.field_name} className="space-y-2">
            {field.options?.map((option) => (
              <label
                key={option}
                className="flex items-center gap-2 p-3 rounded-sm border border-[#E2E8E0] bg-[#F7F8F6] cursor-pointer"
              >
                <input
                  type="checkbox"
                  name={field.field_name}
                  value={option}
                  checked={value.split(",").includes(option)}
                  onChange={(e) => {
                    const currentValues = value ? value.split(",") : [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter((v) => v !== option);
                    handleChange(field.field_name, newValues.join(","));
                  }}
                  className="w-4 h-4 accent-[#1F4E3D] rounded"
                />
                <span className="text-sm text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case "date":
        return (
          <input
            key={field.field_name}
            type="date"
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            className={inputClass}
          />
        );

      case "number":
        return (
          <input
            key={field.field_name}
            type="number"
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
            className={inputClass}
          />
        );

      case "file":
      case "image":
        return (
          <div key={field.field_name}>
            <label
              htmlFor={`file-${field.field_name}`}
              className="flex flex-col items-center justify-center w-full px-4 py-6 bg-[#F7F8F6] border-2 border-dashed border-[#D8DED8] rounded-sm cursor-pointer hover:bg-[#C2A04B]/5 hover:border-[#C2A04B]/50 transition-colors"
            >
              {uploadingFields[field.field_name] ? (
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-4 border-[#C2A04B] border-t-transparent rounded-full animate-spin mb-2" />
                  <span className="text-sm text-gray-500">上传中...</span>
                </div>
              ) : value ? (
                <div className="flex flex-col items-center">
                  {field.field_type === "image" ? (
                    <img src={value} alt="预览" className="w-24 h-24 object-cover rounded-sm mb-2" />
                  ) : (
                    <i className="ri-file-check-line text-3xl text-[#1F4E3D] mb-2" />
                  )}
                  <span className="text-sm text-gray-600">点击重新上传</span>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <i className="ri-upload-cloud-2-line text-3xl text-[#C2A04B] mb-2" />
                  <span className="text-sm text-gray-500">{field.placeholder || `点击上传${field.field_type === "image" ? "图片" : "文件"}`}</span>
                  {field.hint && <span className="text-xs text-gray-400 mt-1">{field.hint}</span>}
                </div>
              )}
            </label>
            <input
              id={`file-${field.field_name}`}
              type="file"
              accept={field.field_type === "image" ? "image/*" : "*/*"}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(field.field_name, file);
              }}
              className="hidden"
            />
            {value && (
              <button type="button" onClick={() => handleChange(field.field_name, "")} className="mt-2 text-sm text-[#8C6E2E] hover:text-[#C2A04B] cursor-pointer">
                清除
              </button>
            )}
          </div>
        );

      default:
        return (
          <div className="relative">
            <input
              key={field.field_name}
              type={isPassword ? (showPassword ? "text" : "password") : "text"}
              value={value}
              onChange={(e) => handleChange(field.field_name, e.target.value)}
              placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
              className={isPassword ? `${inputClass} pr-11` : inputClass}
            />
            {isPassword && (
              <button
                type="button"
                onClick={() => togglePasswordVisibility(field.field_name)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className={showPassword ? "ri-eye-line" : "ri-eye-off-line"} />
              </button>
            )}
          </div>
        );
    }
  };

  const validate = () => {
    const currentData = formDataRef.current;
    if (!agreed) { setError("请先阅读并同意注册协议和隐私政策"); return false; }
    for (const field of fields) {
      if (field.is_required && !(currentData[field.field_name] || "").trim()) {
        setError(`${field.field_label}不能为空`);
        return false;
      }
    }
    if (smsEnabled) {
      const phone = currentData['phone'] || '';
      const smsCode = currentData['sms_code'] || '';
      if (!phone) { setError("请输入手机号"); return false; }
      if (!/^1[3-9]\d{9}$/.test(phone)) { setError("手机号格式不正确"); return false; }
      if (!smsCode) { setError("请输入短信验证码"); return false; }
      if (smsCode.length !== 6) { setError("短信验证码为6位数字"); return false; }
    }
    const usernameField = fields.find((f) => f.field_name === "username");
    if (usernameField && currentData.username && !/^\w{3,20}$/.test(currentData.username)) {
      setError("用户名只能包含字母、数字和下划线，长度3-20位");
      return false;
    }
    if (currentData.password && currentData.password.length < 6) {
      setError("密码长度至少6位");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!validate()) return;
    setIsLoading(true);
    try {
      const currentData = formDataRef.current;
      const params: Record<string, string | undefined> = {
        username: currentData.username,
        password: currentData.password,
        site_id: getSiteId(),
        confirm_password: currentData.confirm_password || '',
      };
      fields.forEach((field) => {
        if (!["username", "password", "nickname", "confirm_password"].includes(field.field_name)) {
          params[field.field_name] = currentData[field.field_name] ?? '';
        }
      });
      if (smsEnabled) {
        params.phone = currentData['phone'];
        params.sms_code = currentData['sms_code'];
      }
      await registerApi(params);
      navigate("/");
    } catch (err: any) {
      setError(err.message || "注册失败，请稍后重试");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-[#F5F6F4] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-[#C2A04B]" />
          <p className="mt-2 text-gray-500 text-sm">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-[#F5F6F4]">
      {/* ── 左侧品牌区：深墨绿 ── */}
      <div className="hidden lg:flex w-[55%] relative flex-col items-center justify-center p-12 bg-gradient-to-br from-[#1F4E3D] via-[#1A4334] to-[#12382A] overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#C2A04B] via-[#DDC583] to-[#C2A04B]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 1px, transparent 14px)" }} />
        <div className="absolute top-16 right-16 w-72 h-72 rounded-full bg-[#C2A04B]/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-[#C2A04B]/5 blur-3xl" />

        <div className="relative z-10 max-w-md">
          <div className="mb-10">
            {siteLogo ? (
              <img src={siteLogo} alt={siteDisplayName} className="h-14" />
            ) : (
              <div className="w-20 h-20 rounded-sm bg-gradient-to-br from-[#C2A04B] to-[#8C6E2E] flex items-center justify-center text-white text-3xl font-black shadow-2xl shadow-[#C2A04B]/30 border border-[#DDC583]/40">
                {siteDisplayName ? siteDisplayName.charAt(0).toUpperCase() : 'E'}
              </div>
            )}
          </div>

          <h1 className="text-4xl font-black text-white mb-4 leading-tight">
            加入<span className="text-[#DDC583]">{siteDisplayName || "我们"}</span>
          </h1>
          <p className="text-white/60 leading-relaxed text-sm">注册账户，开启您的分类信息服务</p>

          <div className="mt-12 grid grid-cols-2 gap-4 text-left">
            <div className="bg-white/5 rounded-sm p-4 border border-[#C2A04B]/20 backdrop-blur-sm">
              <div className="w-9 h-9 rounded-sm bg-[#C2A04B] flex items-center justify-center mb-3">
                <i className="ri-file-list-line text-[#1F4E3D]" />
              </div>
              <div className="text-sm font-semibold text-white">一键发布</div>
              <div className="text-xs text-white/50 mt-1">简单流程快速上架</div>
            </div>
            <div className="bg-white/5 rounded-sm p-4 border border-[#C2A04B]/20 backdrop-blur-sm">
              <div className="w-9 h-9 rounded-sm bg-gradient-to-br from-[#C2A04B] to-[#A8863A] flex items-center justify-center mb-3">
                <i className="ri-shield-check-line text-[#1F4E3D]" />
              </div>
              <div className="text-sm font-semibold text-white">安全可靠</div>
              <div className="text-xs text-white/50 mt-1">信息审核机制保障</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 右侧注册表单 ── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 relative z-10">
        <div className="w-full max-w-md">
          {/* 移动端 Logo */}
          <div className="lg:hidden mb-6 text-center">
            {siteLogo ? (
              <img src={siteLogo} alt="" className="h-10 mx-auto mb-2" />
            ) : (
              <div className="w-14 h-14 rounded-sm bg-gradient-to-br from-[#C2A04B] to-[#8C6E2E] mx-auto flex items-center justify-center text-white text-xl font-black shadow-lg shadow-[#C2A04B]/20">
                {siteDisplayName ? siteDisplayName.charAt(0).toUpperCase() : 'E'}
              </div>
            )}
            {siteDisplayName && <p className="text-sm text-gray-500 mt-2">{siteDisplayName}</p>}
          </div>

          <div className="bg-white rounded-md shadow-2xl shadow-[#1F4E3D]/10 border border-[#E2E8E0] p-7 sm:p-9">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-1 h-6 bg-[#C2A04B] rounded-full" />
              <h2 className="text-2xl font-black text-[#1F4E3D]">创建账户</h2>
            </div>
            <p className="text-sm text-gray-400 mb-7">填写以下信息完成注册</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 动态字段 */}
              {fields.map((field) => (
                <div key={field.field_name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {field.field_label}
                    {field.is_required && <span className="text-red-500">*</span>}
                  </label>
                  <div className="relative">
                    <i className={`${getFieldIcon(field.field_type)} absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C2A04B] z-10`} />
                    {renderField(field)}
                  </div>
                  {field.hint && <p className="mt-1 text-xs text-gray-400">{field.hint}</p>}
                </div>
              ))}

              {/* 短信验证码 */}
              {smsEnabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      手机号 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C2A04B] z-10" />
                      <input
                        type="tel"
                        value={formData['phone'] || ''}
                        onChange={(e) => { handleChange('phone', e.target.value); setError(""); }}
                        placeholder="请输入手机号"
                        className="w-full pl-11 pr-28 py-3.5 bg-[#F7F8F6] border border-[#E2E8E0] rounded-sm text-sm focus:outline-none focus:border-[#C2A04B] focus:ring-4 focus:ring-[#C2A04B]/10 transition-all"
                      />
                      <button
                        type="button"
                        onClick={handleSendSmsCode}
                        disabled={smsCountdown > 0 || smsLoading}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-2 text-xs bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] font-semibold rounded-sm hover:from-[#D8B86A] hover:to-[#B8912F] disabled:from-gray-300 disabled:to-gray-300 disabled:text-gray-500 transition-all cursor-pointer whitespace-nowrap"
                      >
                        {smsLoading ? '发送中...' : smsCountdown > 0 ? `${smsCountdown}s后重发` : '获取验证码'}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      短信验证码 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <i className="ri-shield-check-line absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C2A04B] z-10" />
                      <input
                        type="text"
                        value={formData['sms_code'] || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          handleChange('sms_code', value);
                          setError("");
                        }}
                        placeholder="请输入6位验证码"
                        maxLength={6}
                        className="w-full pl-11 pr-4 py-3.5 bg-[#F7F8F6] border border-[#E2E8E0] rounded-sm text-sm focus:outline-none focus:border-[#C2A04B] focus:ring-4 focus:ring-[#C2A04B]/10 transition-all tracking-widest"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* 错误提示 */}
              {error && (
                <div className="p-3 bg-red-50 text-red-600 text-sm rounded-sm flex items-center gap-2">
                  <i className="ri-error-warning-line shrink-0" />
                  {error}
                </div>
              )}

              {/* 协议勾选 */}
              <div
                className={`flex items-start gap-2.5 p-3 rounded-sm border transition-colors cursor-pointer ${
                  agreed ? "border-[#C2A04B] bg-[#C2A04B]/5" : "border-[#E2E8E0] bg-[#F7F8F6]"
                }`}
                onClick={() => setAgreed(!agreed)}
              >
                <div className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${agreed ? "border-[#1F4E3D] bg-[#1F4E3D]" : "border-gray-300 bg-white"}`}>
                  {agreed && <i className="ri-check-line text-[#DDC583] text-xs" />}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed select-none">
                  我已阅读并同意
                  <span className="text-[#8C6E2E] hover:underline mx-1 cursor-pointer font-medium" onClick={(e) => { e.stopPropagation(); openAgreement("register-agreement", "注册协议"); }}>
                    《注册协议》
                  </span>
                  和
                  <span className="text-[#8C6E2E] hover:underline mx-1 cursor-pointer font-medium" onClick={(e) => { e.stopPropagation(); openAgreement("privacy", "隐私政策"); }}>
                    《隐私政策》
                  </span>
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] font-bold rounded-sm hover:from-[#D8B86A] hover:to-[#B8912F] transition-all shadow-lg shadow-[#C2A04B]/25 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />注册中...
                  </span>
                ) : "立即注册"}
              </button>
            </form>

            <div className="mt-6 text-center flex items-center justify-between">
              <Link to="/" className="text-sm text-gray-400 hover:text-[#C2A04B] inline-flex items-center gap-1 transition-colors">
                <i className="ri-arrow-left-line" />返回首页
              </Link>
              <p className="text-sm text-gray-400">
                已有账号？{" "}
                <Link to="/login" className="text-[#8C6E2E] hover:text-[#C2A04B] font-semibold">登录</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── 协议弹窗 ── */}
      {agreementModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={closeAgreement}>
          <div className="bg-white rounded-md shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EDF0EC] shrink-0">
              <h2 className="text-lg font-bold text-[#1F4E3D] flex items-center gap-2">
                <i className="ri-file-text-line text-[#C2A04B]" />
                {agreementModal.title}
              </h2>
              <button onClick={closeAgreement} className="w-8 h-8 flex items-center justify-center rounded-sm text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {agreementModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <i className="ri-loader-4-line animate-spin text-2xl text-[#C2A04B]" />
                    <p className="mt-2 text-sm text-gray-400">加载中...</p>
                  </div>
                </div>
              ) : (
                <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: agreementModal.content }} />
              )}
            </div>
            <div className="px-6 py-4 border-t border-[#EDF0EC] shrink-0 flex justify-end">
              <button onClick={closeAgreement} className="px-6 py-2.5 bg-gradient-to-r from-[#C2A04B] to-[#A8863A] text-[#1F4E3D] font-semibold rounded-sm hover:from-[#D8B86A] hover:to-[#B8912F] transition-all text-sm cursor-pointer">
                已了解，关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
